import { Directive, HostBinding, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MuiModalService } from '@move/ui';
import { TranslateService } from '@ngx-translate/core';
import { NEVER, Observable, of, Subject, Subscription, switchMap, timer } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';


/**
 * Dashboard Widgets should extend this class to share common functionality between widgets.
 */
@Directive()
export abstract class MmuDashboardWidget<C = unknown, R = void> implements OnInit, OnDestroy {

  @Input()
  /** The widget config that will be loaded and injected from the API. */
  set config(config: C | null) {
    this._config = config;
  }

  get config(): C | null {
    return this._config;
  }

  @HostBinding('attr.data-synced-at')
  lastUpdate: string | null = null;

  @HostBinding('class.mui-placeholder')
  get _muiPlaceholderClass(): boolean {
    return !this.initialized;
  }

  /** The widget ID when rendered on the dashboard */
  widgetId!: string;

  /** This flag indicates whether the widget has finished loading its initial data. */
  initialized = true;

  /** This is the widget's HTML tag. Will be set automatically by the DashboardWidget decorator. */
  readonly tag!: string;

  /** This contains the translated names from the widget spec. Will be set automatically by the DashboardWidget decorator. */
  readonly title!: string;
  currentLangTitle!: string;

  /** This contains the translated descriptions from the widget spec. Will be set automatically by the DashboardWidget
   * decorator.
   */
  readonly description!: string;
  currentLangDescription!: string;

  protected _config: C | null = null;

  /** The time in ms the polling of the widget should refresh. */
  protected refreshInterval = 5000;
  protected destroy$ = new Subject<void>();

  private pollInterval$: Observable<R> = of();
  private subscription: Subscription | null = null;

  /**
   * This property must be set by descendants that want to poll data. The Observable will be subscribed to every X seconds as
   * defined in the refreshInterval property.
   */
  protected api$: Observable<R> = NEVER;
  protected translateService: TranslateService | null = inject(TranslateService, { optional: true });
  protected modalService: MuiModalService | null = inject(MuiModalService, { optional: true });

  ngOnInit(): void {
    this.initI18nHelpers();

    if (!this.api$) {
      return;
    }

    this.startPolling();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startPolling(): void {
    if (!this.subscription) {
      this.initialized = false;
      this.pollInterval$ = timer(0, this.refreshInterval).pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.api$.pipe(finalize(() => { this.initialized = true; }))),
        tap((data) => {
          this.lastUpdate = new Date().toISOString();
          this.update(data);
        })
      );
      this.subscription = this.pollInterval$.subscribe();
    }
  }

  stopPolling(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  restartPolling(): void {
    this.stopPolling();
    this.startPolling();
  }

  private initI18nHelpers(): void {
    if (this.translateService) {
      this.currentLangTitle = this.title;
      this.currentLangDescription = this.description;

      this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.currentLangTitle = this.title;
        this.currentLangDescription = this.description;
      });
    }
  }

  protected abstract update(data: R): void;
}
